import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { safeLocal } from "../lib/safeStorage";

const RealtimeCtx = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const RealtimeProvider = ({ children }) => {
  const { user } = useAuth();
  const [event, setEvent] = useState(null); // last received event
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const aliveRef = useRef(true);

  const send = (data) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      try { ws.send(typeof data === "string" ? data : JSON.stringify(data)); } catch {}
    }
  };

  useEffect(() => {
    aliveRef.current = true;
    const token = safeLocal.get("ss_token");
    const approved = user && (user.role === "admin" || user.status === "approved");
    if (!approved || !token) return;

    const wsUrl = BACKEND_URL.replace(/^http/, "ws") + "/api/ws?auth=" + token;

    let pingTimer;
    const connect = () => {
      if (!aliveRef.current) return;
      let ws;
      try { ws = new WebSocket(wsUrl); } catch { return; }
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        retryRef.current = 0;
        pingTimer = setInterval(() => { try { ws.send("ping"); } catch {} }, 25000);
      };
      ws.onmessage = (e) => {
        try { setEvent({ ...JSON.parse(e.data), _t: Date.now() }); } catch {}
      };
      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingTimer);
        if (!aliveRef.current) return;
        const delay = Math.min(1000 * 2 ** retryRef.current, 15000);
        retryRef.current += 1;
        setTimeout(connect, delay);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();

    return () => {
      aliveRef.current = false;
      clearInterval(pingTimer);
      try { wsRef.current && wsRef.current.close(); } catch {}
    };
  }, [user]);

  return (
    <RealtimeCtx.Provider value={{ event, connected, send }}>
      {children}
    </RealtimeCtx.Provider>
  );
};

export const useRealtime = () => useContext(RealtimeCtx);
